import { useLocale } from '@solinkcorp/locale';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@solinkcorp/solink-ui/components/Collapsible';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import { ChevronDownIcon } from 'lucide-react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { DEFAULT_COUNT_THRESHOLD, FIXED_DRIVE_OFF_THRESHOLD } from '../../constants';
import { getScheduleConflictLabels, type ServiceDisplayDayPart } from '../../day-part/utils';
import { type DayPartThreshold, type ServiceDisplayWidget } from '../../layout/types';
import { ThresholdRow, type WidgetThresholdType } from './ThresholdRow';

/** Computes disabled state and conflict message for an unchecked row that would overlap enabled day parts. */
function getRowConflictState(
  dayPart: ServiceDisplayDayPart,
  state: RowState,
  allDayParts: ServiceDisplayDayPart[],
  checkedIds: Set<string>,
  formatMessage: (labels: string[]) => string,
): {
  disabledByOverlap: boolean;
  conflictMessage: string | undefined;
  conflictLabelCount: number;
} {
  if (state.checked) {
    return { disabledByOverlap: false, conflictMessage: undefined, conflictLabelCount: 0 };
  }
  const conflictLabels = getScheduleConflictLabels(dayPart.dayParts, allDayParts, {
    excludeDayPartId: dayPart.id,
    onlyIncludeDayPartIds: checkedIds,
  });
  const disabledByOverlap = conflictLabels.length > 0;
  const conflictMessage = disabledByOverlap ? formatMessage(conflictLabels) : undefined;
  return {
    disabledByOverlap,
    conflictMessage,
    conflictLabelCount: conflictLabels.length,
  };
}

export interface ThresholdComponentProps {
  widgetType: WidgetThresholdType;
  widget: ServiceDisplayWidget;
  allDayParts: ServiceDisplayDayPart[];
  thresholds?: DayPartThreshold[];
  onThresholdsChange?: (thresholds: DayPartThreshold[]) => void;
  dayPartsEnabledInOtherWidgetsInLayout?: ServiceDisplayDayPart[];
}

interface RowState {
  checked: boolean;
  countThreshold: number;
  timeMinutes: number;
  timeSeconds: number;
}

const DEFAULT_ROW_STATE: RowState = {
  checked: false,
  countThreshold: 0,
  timeMinutes: 0,
  timeSeconds: 0,
};

function buildThresholdsFromRowState(
  rowStateByDayPartId: Record<string, RowState>,
  allDayParts: ServiceDisplayDayPart[],
  widgetType: WidgetThresholdType,
  existingThresholds?: DayPartThreshold[],
): DayPartThreshold[] {
  const type = widgetType === 'DRIVE_OFF' ? 'COUNT' : widgetType;
  const byDayPartId = new Map<string, DayPartThreshold>();
  for (const t of existingThresholds ?? []) {
    byDayPartId.set(t.dayPartsId, t);
  }
  const result: DayPartThreshold[] = [];
  for (const dayPart of allDayParts) {
    const state = rowStateByDayPartId[dayPart.id] ?? DEFAULT_ROW_STATE;
    if (!state.checked) continue;
    // DRIVE_OFF widgets don't expose a threshold input — the row is association-only. Persist
    // the fixed sentinel so the back-end always receives a stable value regardless of whatever
    // `state.countThreshold` happens to hold (which is sourced from `widget.config.threshold`
    // and can legitimately be 0 or any other historical value).
    const threshold =
      widgetType === 'DRIVE_OFF'
        ? FIXED_DRIVE_OFF_THRESHOLD
        : type === 'TIME'
          ? state.timeMinutes * 60 + state.timeSeconds
          : state.countThreshold;
    const existing = byDayPartId.get(dayPart.id);
    const entry: DayPartThreshold = { dayPartsId: dayPart.id, threshold, type };
    if (existing?.id) entry.id = existing.id;
    result.push(entry);
  }
  return result;
}

function rowStateFromThresholds(
  thresholds: DayPartThreshold[] | undefined,
  allDayParts: ServiceDisplayDayPart[],
  widgetType: WidgetThresholdType,
): Record<string, RowState> {
  const byDayPartId = new Map<string, DayPartThreshold>();
  for (const t of thresholds ?? []) {
    byDayPartId.set(t.dayPartsId, t);
  }
  const type = widgetType === 'DRIVE_OFF' ? 'COUNT' : widgetType;
  const out: Record<string, RowState> = {};
  for (const dayPart of allDayParts) {
    const t = byDayPartId.get(dayPart.id);
    if (!t) {
      out[dayPart.id] = { ...DEFAULT_ROW_STATE };
      continue;
    }
    if (type === 'TIME') {
      const totalSeconds = t.threshold;
      out[dayPart.id] = {
        checked: true,
        countThreshold: 0,
        timeMinutes: Math.floor(totalSeconds / 60),
        timeSeconds: totalSeconds % 60,
      };
    } else {
      out[dayPart.id] = {
        checked: true,
        countThreshold: t.threshold,
        timeMinutes: 0,
        timeSeconds: 0,
      };
    }
  }
  return out;
}

function getDefaultThresholdsFromWidget(
  widget: ServiceDisplayWidget,
  widgetType: WidgetThresholdType,
): { defaultCountThreshold: number; defaultTimeMinutes: number; defaultTimeSeconds: number } {
  const config = widget.config;
  if (widgetType === 'TIME') {
    if (config.type === 'TIME' || config.type === 'TOTAL_JOURNEY_TIME') {
      const sec = config.threshold;
      return {
        defaultCountThreshold: 0,
        defaultTimeMinutes: Math.floor(sec / 60),
        defaultTimeSeconds: sec % 60,
      };
    }
    return { defaultCountThreshold: 0, defaultTimeMinutes: 0, defaultTimeSeconds: 0 };
  }
  return {
    defaultCountThreshold:
      config.type === 'COUNT' || config.type === 'DRIVE_OFF'
        ? config.threshold
        : DEFAULT_COUNT_THRESHOLD,
    defaultTimeMinutes: 0,
    defaultTimeSeconds: 0,
  };
}

export const ThresholdComponent: React.FC<ThresholdComponentProps> = ({
  widgetType,
  widget,
  allDayParts,
  thresholds,
  onThresholdsChange,
  dayPartsEnabledInOtherWidgetsInLayout,
}) => {
  const locale = useLocale();

  const initialRowState = useMemo(
    () => rowStateFromThresholds(thresholds, allDayParts, widgetType),
    [thresholds, allDayParts, widgetType],
  );
  // Set state is mount time only, form dialog must be closed and reopened for any new widget thresholds to render
  // Want to keep it this way so a user doesn't lose their changes in threshold when updating widget.threshold value (fallback value parent level)
  const [rowStateByDayPartId, setRowStateByDayPartId] =
    useState<Record<string, RowState>>(initialRowState);

  const updateRow = useCallback(
    (dayPartId: string, patch: Partial<RowState>) => {
      setRowStateByDayPartId((prev: Record<string, RowState>) => {
        const current = prev[dayPartId] ?? DEFAULT_ROW_STATE;
        let merged = { ...current, ...patch };
        if (patch.checked === true) {
          const defaults = getDefaultThresholdsFromWidget(widget, widgetType);
          if (widgetType === 'TIME') {
            merged = {
              ...merged,
              timeMinutes: defaults.defaultTimeMinutes,
              timeSeconds: defaults.defaultTimeSeconds,
            };
          } else {
            merged = { ...merged, countThreshold: defaults.defaultCountThreshold };
          }
        }
        const next = { ...prev, [dayPartId]: merged };
        const nextThresholds = buildThresholdsFromRowState(
          next,
          allDayParts,
          widgetType,
          thresholds,
        );
        onThresholdsChange?.(nextThresholds);
        return next;
      });
    },
    [allDayParts, widgetType, widget, thresholds, onThresholdsChange],
  );

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, []);

  const checkedIds = useMemo(
    () =>
      new Set(
        allDayParts
          .filter((dp) => (rowStateByDayPartId[dp.id] ?? DEFAULT_ROW_STATE).checked)
          .map((dp) => dp.id),
      ),
    [allDayParts, rowStateByDayPartId],
  );

  // Merge this widget's checked day parts with other widgets' day parts in layout (recompute every render so layout-wide conflicts are always current).
  const conflictCheckIds = new Set([
    ...checkedIds,
    ...(dayPartsEnabledInOtherWidgetsInLayout ?? []).map((d) => d.id),
  ]);

  return (
    <div ref={containerRef} data-testid="day-part-threshold-panel">
      <Collapsible open={open} onOpenChange={handleOpenChange} className="w-full">
        <div
          className={cn(
            'overflow-hidden rounded-lg border border-border bg-background-surface',
            'flex flex-col gap-4 p-4',
          )}
        >
          <CollapsibleTrigger
            className="flex w-full items-center justify-between gap-4 rounded outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-expanded={open}
          >
            <p className="shrink-0 text-sm font-semibold leading-tight text-foreground">
              {widgetType === 'DRIVE_OFF'
                ? locale.serviceDisplayMonitor.dayPartAssignment
                : locale.serviceDisplayMonitor.dayPartSpecificThresholds}
            </p>
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg p-1">
              <ChevronDownIcon
                className={cn(
                  'size-4 text-interaction-neutral-content-default transition-transform',
                  open && 'rotate-180',
                )}
              />
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="flex flex-col gap-4">
              {allDayParts.length === 0 ? (
                <p
                  className="text-sm text-muted-foreground"
                  data-testid="day-part-threshold-info-text"
                >
                  {locale.serviceDisplayMonitor.dayPartThresholdsEmpty}
                </p>
              ) : (
                <>
                  <div
                    className="flex max-h-[240px] flex-col gap-3 overflow-y-auto overflow-x-hidden p-1"
                    role="list"
                  >
                    {[...allDayParts]
                      .sort((a, b) => {
                        const aStart = Object.values(a.dayParts).flat()[0]?.start ?? '';
                        const bStart = Object.values(b.dayParts).flat()[0]?.start ?? '';
                        return aStart.localeCompare(bStart);
                      })
                      .map((dayPart: ServiceDisplayDayPart) => {
                        const state = rowStateByDayPartId[dayPart.id] ?? DEFAULT_ROW_STATE;
                        const conflict = getRowConflictState(
                          dayPart,
                          state,
                          allDayParts,
                          conflictCheckIds,
                          (labels) =>
                            locale.serviceDisplayMonitor.dayPartOverlapConflictLabels.replace(
                              '{{labels}}',
                              labels.join(', '),
                            ),
                        );

                        return (
                          <div key={dayPart.id} role="listitem">
                            <ThresholdRow
                              dayPart={dayPart}
                              widgetType={widgetType}
                              checked={state.checked}
                              onCheckedChange={(checked: boolean) =>
                                updateRow(dayPart.id, { checked })
                              }
                              countThreshold={state.countThreshold}
                              onCountThresholdChange={(value: number) =>
                                updateRow(dayPart.id, { countThreshold: value })
                              }
                              timeThresholdMinutes={state.timeMinutes}
                              timeThresholdSeconds={state.timeSeconds}
                              onTimeThresholdChange={(minutes: number, seconds: number) =>
                                updateRow(dayPart.id, {
                                  timeMinutes: minutes,
                                  timeSeconds: seconds,
                                })
                              }
                              disabled={conflict.disabledByOverlap}
                              conflictMessage={conflict.conflictMessage}
                              conflictLabelCount={conflict.conflictLabelCount}
                            />
                          </div>
                        );
                      })}
                  </div>
                </>
              )}
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  );
};
